import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { verifyPublicOrderPayment } from "../api/client";

/**
 * Where Paystack/Flutterwave redirect the browser after a receiver completes
 * checkout from the public share link (see PublicOrderView.js). Separate
 * from the authenticated /payment-callback — the receiver has no account,
 * so this reads the share token straight out of the reference string
 * (format "pubpay_<token>_<timestamp>") instead of looking anything up by a
 * logged-in user.
 */
export default function PublicPaymentCallback() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // "verifying" | "success" | "failed"
  const [message, setMessage] = useState("Confirming your payment...");
  const [token, setToken] = useState(null);

  useEffect(() => {
    async function run() {
      const reference = params.get("reference") || params.get("trxref") || params.get("tx_ref");
      const gateway = params.get("trxref") ? "paystack" : params.get("tx_ref") ? "flutterwave" : "paystack";
      const flwStatus = params.get("status");

      if (!reference || !reference.startsWith("pubpay_")) {
        setStatus("failed");
        setMessage("No payment reference was returned — the payment may have been cancelled.");
        return;
      }
      const isFlwSuccess = flwStatus && ["successful", "completed"].includes(flwStatus.toLowerCase());
      if (flwStatus && !isFlwSuccess) {
        setStatus("failed");
        setMessage("Payment was not completed.");
        return;
      }

      const shareToken = reference.split("_")[1];
      setToken(shareToken);

      try {
        await verifyPublicOrderPayment(shareToken, reference, gateway);
        setStatus("success");
        setMessage("Payment received — thank you for covering the delivery fee!");
      } catch (err) {
        setStatus("failed");
        setMessage(err.response?.data?.error || "We couldn't confirm this payment. If you were charged, please contact the sender.");
      }
    }
    run();
  }, [params]);

  return (
    <div className="page" style={{ textAlign: "center" }}>
      <div className="brand-header">
        <span className="brand-logo">{status === "verifying" ? "⏳" : status === "success" ? "✅" : "⚠️"}</span>
        <span className="brand-name">
          {status === "verifying" ? "Confirming Payment" : status === "success" ? "Payment Successful" : "Payment Issue"}
        </span>
      </div>
      <p className={status === "failed" ? "error" : "hint"} style={{ fontSize: "15px" }}>{message}</p>
      {status !== "verifying" && token && (
        <Link to={`/share/${token}`}>
          <button style={{ marginTop: "16px" }}>Back to Delivery Details</button>
        </Link>
      )}
    </div>
  );
}
